import { useBuild, useRetriesForJob } from "~/components/build/Show/lib/BuildContext";
import { Link, useNavigate } from "react-router-dom";
import BuildPageStore from "~/stores/BuildPageStore";
import Job, { type Props as JobComponentProps } from "~/components/job/Job";
import { useCallback, useEffect } from "react";
import { RetryableJob } from "~/components/build/Show/lib/types";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import Icon from "~/components/shared/Icon";
import ToolbarButton from "~/components/build/Show/components/ToolbarButton";
import { useQueryClient } from "@tanstack/react-query";
import { LogLineSelectionProvider } from "~/components/build/Show/lib/useLogLineSelection";
import { jobQueryOptions } from "~/components/build/Show/lib/queries/job";
import { replicaLagRetryOptions } from "~/components/build/Show/lib/replicaLagRetry";
import { useJobTabSync } from "~/components/build/Show/components/StepDrawer/useJobTabSync";

type Props = {
  job: RetryableJob | null | undefined;
  // It is being used!
  // eslint-disable-next-line react/no-unused-prop-types
  jobWithRetries?: RetryableJob[];
};

export const RetryJobNavigation = (props: Props) => {
  const { build } = useBuild();
  const queryClient = useQueryClient();

  if (!props.job) {
    return null;
  }

  const jobWithRetries = props.jobWithRetries || useRetriesForJob(props.job);
  const index = jobWithRetries.findIndex((job) => job.id === props.job?.id);

  const prev = jobWithRetries[index - 1];
  const next = jobWithRetries[index + 1];

  if (jobWithRetries.length <= 1) {
    return null;
  }

  return (
    <div className="ml-auto flex items-center gap-2">
      <span className="text-xs">
        Retry <span className="tabular-nums">{index + 1}</span>
        {" of "}
        <span className="tabular-nums">{jobWithRetries.length}</span>
      </span>

      <div className="flex gap-1">
        <Link
          data-testid="previous-retry-link"
          to={prev && resolveJobUrl(prev.id)}
          onMouseEnter={() => {
            if (Features.ProgressiveBuildShow && prev) {
              queryClient.prefetchQuery(jobQueryOptions(build, prev.id));
            }
          }}
          title="Previous"
          aria-disabled={!prev}
          className="text-inherit aria-disabled:pointer-events-none aria-disabled:text-gray-700"
        >
          <ToolbarButton>
            <Icon icon="heroicons/outline/chevron-left" className="h-4" />
          </ToolbarButton>
        </Link>
        <Link
          data-testid="next-retry-link"
          to={next && resolveJobUrl(next.id)}
          onMouseEnter={() => {
            if (Features.ProgressiveBuildShow && next) {
              queryClient.prefetchQuery(jobQueryOptions(build, next.id));
            }
          }}
          title="Next"
          aria-disabled={!next}
          className="text-inherit aria-disabled:pointer-events-none aria-disabled:text-gray-700"
        >
          <ToolbarButton>
            <Icon icon="heroicons/outline/chevron-right" className="h-4" />
          </ToolbarButton>
        </Link>
      </div>
    </div>
  );
};

export const CommandJobDetails = ({ job }: Props) => {
  const { build, setBuildData, reloadBuild } = useBuild();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Sync tab between URL and store, get active tab and change handler
  const { activeTab, onTabChange } = Features.BuildJobTabUrls
    ? useJobTabSync({ job })
    : { activeTab: undefined, onTabChange: undefined };

  useEffect(() => {
    if (job) {
      BuildPageStore.expandJob(job);
    }
  }, [job?.id]);

  const handleRedirectToLatestJob = useCallback(
    (latestJobUUID: string) => {
      if (latestJobUUID) {
        if (Features.ProgressiveBuildShow) {
          // Fetch the job data before navigating to prevent a flash of error state.
          // The new job may be temporarily unavailable due to replica lag.
          queryClient
            .fetchQuery(jobQueryOptions(build, latestJobUUID, replicaLagRetryOptions))
            .then(() => {
              navigate(resolveJobUrl(latestJobUUID));
            })
            .catch(() => {
              // If the job still can't be fetched after retries, navigate anyway
              // so the drawer can show the error state rather than silently failing.
              navigate(resolveJobUrl(latestJobUUID));
            });
        } else {
          navigate(resolveJobUrl(latestJobUUID));
        }
      }
    },
    [build, queryClient, navigate],
  );

  if (!job) {
    return null;
  }

  return (
    <LogLineSelectionProvider>
      <div className="job-list-pipeline flex h-full flex-col">
        <Job
          key={job.id}
          job={job as JobComponentProps["job"]}
          build={build}
          setBuildData={setBuildData}
          autoFollow={true}
          onRetrySuccess={handleRedirectToLatestJob}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      </div>
    </LogLineSelectionProvider>
  );
};
